import apiClient from './apiClient';
import { apiConstants } from '../constants/api.constant';

class NotificationService {
    constructor() {
        // Remove baseURL since apiClient already handles the base URL
        // this.baseURL = '/api/v1'; // REMOVED - causes double /api/v1
        this.requestCache = new Map();
        this.cacheTimeout = 2000; // Reduce from 5s to 2s for fresher data
        this.lastInvalidation = Date.now();
    }

    /**
     * Invalidate cache (call after mutations)
     */
    invalidateCache() {
        console.log('Invalidating notification cache');
        this.requestCache.clear();
        this.lastInvalidation = Date.now();
    }

    /**
     * Get user notifications with filtering and pagination
     */
    async getNotifications(params = {}) {
        try {
            // Don't cache if recently invalidated (within 500ms)
            const timeSinceInvalidation = Date.now() - this.lastInvalidation;
            const shouldSkipCache = timeSinceInvalidation < 500;

            // Create cache key from params
            const cacheKey = JSON.stringify(params);
            const now = Date.now();

            // Check cache first (unless recently invalidated)
            if (!shouldSkipCache && this.requestCache.has(cacheKey)) {
                const cached = this.requestCache.get(cacheKey);
                if (now - cached.timestamp < this.cacheTimeout) {
                    console.log('Returning cached notifications response');
                    return cached.response;
                }
            }
            
            // Use the correct endpoint without double /api/v1
            const response = await apiClient.get(apiConstants.GET_NOTIFICATIONS_LIST, { params });
            
            const result = {
                success: true,
                data: response.data.data || response.data,
                message: "Notifications fetched successfully"
            };
            
            // Cache the response
            this.requestCache.set(cacheKey, {
                response: result,
                timestamp: now
            });
            
            // Clean up old cache entries
            this.cleanupCache();
            
            return result;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch notifications",
                data: []
            };
        }
    }

    /**
     * Clean up old cache entries
     */
    cleanupCache() {
        const now = Date.now();
        for (const [key, value] of this.requestCache.entries()) {
            if (now - value.timestamp > this.cacheTimeout) {
                this.requestCache.delete(key);
            }
        }
    }

    /**
     * Get notification by ID
     */
    async getNotificationById(notificationId) {
        try {
            const response = await apiClient.get(`${apiConstants.GET_NOTIFICATION_BY_ID}/${notificationId}`);
            return {
                success: true,
                data: response.data.data || response.data,
                message: "Notification fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch notification"
            };
        }
    }

    /**
     * Mark notification as read
     */
    async markNotificationRead(notificationId) {
        try {
            const response = await apiClient.patch(`${apiConstants.GET_NOTIFICATION_BY_ID}/${notificationId}/read`);

            // Invalidate cache to force fresh data
            this.invalidateCache();

            return {
                success: true,
                data: response.data,
                message: "Notification marked as read"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to mark notification as read"
            };
        }
    }

    /**
     * Mark all notifications as read
     */
    async markAllNotificationsAsRead() {
        try {
            const response = await apiClient.patch(`${apiConstants.MARK_ALL_NOTIFICATIONS_READ}`);
            return {
                success: true,
                data: response.data,
                message: "All notifications marked as read"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to mark all notifications as read"
            };
        }
    }

    /**
     * Mark multiple notifications as read
     */
    async markNotificationsRead(data) {
        try {
            const response = await apiClient.post(apiConstants.MARK_NOTIFICATION_READ, data);
            return {
                success: true,
                data: response.data,
                message: "Notifications marked as read"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to mark notifications as read"
            };
        }
    }

    /**
     * Send notification
     */
    async sendNotification(notificationData) {
        try {
            const response = await apiClient.post(apiConstants.SEND_NOTIFICATION, notificationData);
            
            // Clear cache since new notification was sent
            this.requestCache.clear();
            
            return {
                success: true,
                data: response.data,
                message: "Notification sent successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to send notification"
            };
        }
    }

    /**
     * Create notification with full configuration
     */
    async createNotification(notificationData) {
        try {
            const response = await apiClient.post(apiConstants.CREATE_NOTIFICATION, notificationData);
            return {
                success: true,
                data: response.data,
                message: "Notification created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create notification"
            };
        }
    }

    /**
     * Delete notification
     */
    async deleteNotification(notificationId) {
        try {
            const response = await apiClient.delete(`${apiConstants.GET_NOTIFICATION_BY_ID}/${notificationId}`);

            // Invalidate cache to force fresh data
            this.invalidateCache();

            return {
                success: true,
                data: response.data,
                message: "Notification deleted successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to delete notification"
            };
        }
    }

    /**
     * Acknowledge notification
     */
    async acknowledgeNotification(notificationId, acknowledgmentData = {}) {
        try {
            const response = await apiClient.post(`${apiConstants.GET_NOTIFICATION_BY_ID}/${notificationId}/acknowledge`, acknowledgmentData);
            return {
                success: true,
                data: response.data,
                message: "Notification acknowledged successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to acknowledge notification"
            };
        }
    }

    /**
     * Get notification templates
     */
    async getNotificationTemplates(params = {}) {
        try {
            const response = await apiClient.get(apiConstants.GET_NOTIFICATION_TEMPLATES, { params });
            return {
                success: true,
                data: response.data.data || response.data,
                message: "Notification templates fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch notification templates",
                data: []
            };
        }
    }

    /**
     * Get notification preferences
     */
    async getNotificationPreferences() {
        try {
            const response = await apiClient.get(apiConstants.GET_NOTIFICATION_PREFERENCES);
            return {
                success: true,
                data: response.data.data || response.data,
                message: "Notification preferences fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch notification preferences"
            };
        }
    }

    /**
     * Update notification preferences
     */
    async updateNotificationPreferences(preferencesData) {
        try {
            const response = await apiClient.put(apiConstants.UPDATE_NOTIFICATION_PREFERENCES, preferencesData);
            return {
                success: true,
                data: response.data,
                message: "Notification preferences updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update notification preferences"
            };
        }
    }

    /**
     * Send appointment reminder
     */
    async sendAppointmentReminder(appointmentData) {
        try {
            const response = await apiClient.post('/notifications/appointment-reminder', appointmentData);
            return {
                success: true,
                data: response.data,
                message: "Appointment reminder sent successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to send appointment reminder"
            };
        }
    }

    /**
     * Send medication reminder
     */
    async sendMedicationReminder(medicationData) {
        try {
            const response = await apiClient.post('/notifications/medication-reminder', medicationData);
            return {
                success: true,
                data: response.data,
                message: "Medication reminder sent successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to send medication reminder"
            };
        }
    }

    /**
     * Send safety alert
     */
    async sendSafetyAlert(alertData) {
        try {
            const response = await apiClient.post('/notifications/safety-alert', alertData);
            return {
                success: true,
                data: response.data,
                message: "Safety alert sent successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to send safety alert"
            };
        }
    }
}

export const notificationService = new NotificationService();
export default notificationService; 